import { Button, Center, Group, Loader, Stack, Text } from '@mantine/core'
import { IconUsersPlus } from '@tabler/icons-react'
import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { useValidateToken } from '@/hooks/useValidateToken'
import { useAcceptWorkspaceInvitation } from '@/queries/workspaceQueries'
import { RouterPath } from '@/router/paths'
import { useSession } from '@/session/SessionContext'
import { showToast } from '@/theme/notifications'
import { AuthLayout } from '../layout/AuthLayout/AuthLayout'
import { WorkspaceAlertWithLogout } from './WorkspaceAlerts/WorkspaceAlertWithLogout'

const WORKSPACE_ID_PARAM = 'workspace_id'

export const AcceptWorkspaceInvitation = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, refreshSession, workspaceMemberships } = useSession()

  const {
    isTokenValid,
    token,
    email: inviteEmail,
    isPending: isPendingToken
  } = useValidateToken()

  const { mutateAsync: acceptInvitation, isPending: isPendingAccept } =
    useAcceptWorkspaceInvitation()

  const [searchParams] = useSearchParams()
  const workspaceId = searchParams.get(WORKSPACE_ID_PARAM)

  const pendingWorkspaceMembership = useMemo(() => {
    return workspaceMemberships.find(
      (membership) => membership.workspace.id === workspaceId
    )
  }, [workspaceMemberships, workspaceId])

  const handleAcceptInvitation = async () => {
    if (!token || !workspaceId) {
      return
    }

    try {
      await acceptInvitation({
        workspaceId,
        token
      })

      showToast(
        intl.formatMessage({ id: 'workspace.members.invite.accept.success' }),
        'success'
      )

      void navigate(RouterPath.root)
      void refreshSession()
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'workspace.members.invite.accept.error' })
        ),
        'error'
      )
    }
  }

  if (isPendingToken) {
    return (
      <AuthLayout variant="large">
        <Center h={120}>
          <Loader size="md" />
        </Center>
      </AuthLayout>
    )
  }

  if (!isTokenValid) {
    return (
      <WorkspaceAlertWithLogout
        message={
          <FormattedMessage id="workspace.members.invite.accept.tokenExpired" />
        }
      />
    )
  }

  if (inviteEmail && inviteEmail !== user?.email) {
    return (
      <WorkspaceAlertWithLogout
        message={
          <FormattedMessage
            id="workspace.members.invite.accept.emailMismatch"
            values={{
              inviteEmail,
              b: (chunks) => <b>{chunks}</b>
            }}
          />
        }
        redirectToAfterLogin={location.pathname + location.search}
      />
    )
  }

  return (
    <AuthLayout variant="large">
      <Stack align="center" gap="xl">
        <Group wrap="nowrap">
          <IconUsersPlus size={20} />

          <Text size="sm">
            <FormattedMessage
              id="workspace.members.invite.accept.workspaceName"
              values={{
                workspaceName: pendingWorkspaceMembership?.workspace.name,
                b: (chunks) => <b>{chunks}</b>
              }}
            />
          </Text>
        </Group>

        <Button
          miw={200}
          size="sm"
          loading={isPendingAccept}
          onClick={() => void handleAcceptInvitation()}
        >
          <FormattedMessage id="workspace.members.invite.accept.button" />
        </Button>
      </Stack>
    </AuthLayout>
  )
}
